// Cookie consent: which third-party trackers may load in this browser.
//
// Necessary storage (auth, preferences, this choice itself) is always on.
// Cookieless measurement (Vercel Analytics, Speed Insights, Ahrefs Web
// Analytics) sets no cookies, keeps no IP address and can't follow a visitor
// across days or sites, so it runs for everyone (Vercel's in index.tsx, Ahrefs
// via loadCookieless here).
// Analytics = Google Analytics, Microsoft Clarity and Sentry Session Replay.
// Advertising = Meta Pixel, which also feeds the Meta Conversions API Gateway
// hosted by Stape (capig.stape.us) from the browser; nothing on our server sends
// Meta events.
//
// Whether Analytics needs an opt-in depends on the consent policy from
// GET /api/consent-policy (CONSENT_MODE on the server): opt-in in the EEA, UK
// and Switzerland, or everywhere when CONSENT_MODE=opt_in; elsewhere Analytics
// is on until the visitor turns it off. Advertising is always opt-in. A
// visitor's own choice always wins.
//
// None of these scripts are in index.html any more: applyConsent() injects each
// one only once its category is allowed.

export const CONSENT_VERSION = 1;

export interface CookieConsent {
  analytics: boolean;
  advertising: boolean;
  /** ISO time the visitor last chose; null = not decided yet (banner shows). */
  decidedAt: string | null;
  version: number;
}

/** Shape stored under profiles.settings.privacy.cookie_consent. */
export interface CookieConsentSetting {
  analytics: boolean;
  advertising: boolean;
  decided_at: string;
  version: number;
}

export const UNDECIDED_CONSENT: CookieConsent = {
  analytics: false,
  advertising: false,
  decidedAt: null,
  version: CONSENT_VERSION,
};

export const toConsentSetting = (c: CookieConsent): CookieConsentSetting | null =>
  c.decidedAt
    ? { analytics: c.analytics, advertising: c.advertising, decided_at: c.decidedAt, version: c.version }
    : null;

export const fromConsentSetting = (s: unknown): CookieConsent | null => {
  if (!s || typeof s !== 'object') return null;
  const v = s as Partial<CookieConsentSetting>;
  if (typeof v.decided_at !== 'string') return null;
  return {
    analytics: v.analytics === true,
    advertising: v.advertising === true,
    decidedAt: v.decided_at,
    version: typeof v.version === 'number' ? v.version : CONSENT_VERSION,
  };
};

/** Global Privacy Control: a browser-level opt-out of sale/sharing (treated as Advertising off). */
export const isGpcEnabled = (): boolean =>
  typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;

/**
 * What may actually run: the stored choice, with GPC overriding Advertising.
 * Until the visitor chooses, Analytics follows the region's policy
 * (`optInRequired`; true until the policy has loaded) and Advertising stays off.
 */
export const effectiveConsent = (c: CookieConsent, optInRequired: boolean) => ({
  analytics: c.decidedAt ? c.analytics : !optInRequired,
  advertising: !!c.decidedAt && c.advertising && !isGpcEnabled(),
});

/**
 * Asks the server whether this visitor's region needs an opt-in for Analytics.
 * Any failure keeps the strict default (opt-in required).
 */
export const fetchConsentPolicy = async (): Promise<boolean> => {
  try {
    const res = await fetch('/api/consent-policy', { credentials: 'omit' });
    if (!res.ok) return true;
    const body = await res.json();
    return body?.optInRequired !== false;
  } catch {
    return true;
  }
};

// ---------------------------------------------------------------------------
// Loaders

const GA_ID = 'G-5RPZFLP419';
const CLARITY_ID = 'xeeh2wzm8m';
const AHREFS_KEY = 'uLcg9LY66EamDi3ZohfI4g';
const META_PIXEL_ID = '2078116046155617';

const loaded = { cookieless: false, analytics: false, advertising: false };

// GA, Clarity and Ahrefs never ran on localhost. Set localStorage DEBUG_TRACKERS=1
// to load them there anyway (for testing this consent flow).
const isLocalHost = () => {
  if (typeof window === 'undefined') return true;
  const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!local) return false;
  try { return localStorage.getItem('DEBUG_TRACKERS') !== '1'; } catch { return true; }
};

const addScript = (src: string, attrs: Record<string, string> = {}) => {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

const loadAnalytics = () => {
  if (loaded.analytics || isLocalHost()) return;
  loaded.analytics = true;
  const w = window as any;

  // Microsoft Clarity
  w.clarity = w.clarity || function (...args: unknown[]) { (w.clarity.q = w.clarity.q || []).push(args); };
  addScript(`https://www.clarity.ms/tag/${CLARITY_ID}?ref=bwt`);

  // Google Analytics (gtag.js). Page views are sent by App.tsx on route changes.
  w.dataLayer = w.dataLayer || [];
  w.gtag = function () { w.dataLayer.push(arguments); };
  w.gtag('js', new Date());
  w.gtag('config', GA_ID, { send_page_view: false, cookie_domain: 'auto' });
  addScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
};

/** Ahrefs Web Analytics: cookieless, so it loads for every visitor. */
export const loadCookieless = () => {
  if (loaded.cookieless || isLocalHost()) return;
  loaded.cookieless = true;
  addScript('https://analytics.ahrefs.com/analytics.js', { 'data-key': AHREFS_KEY });
};

const loadAdvertising = () => {
  if (loaded.advertising) return;
  loaded.advertising = true;
  const w = window as any;
  if (!w.fbq) {
    const n: any = function (...args: unknown[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    w.fbq = n;
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    addScript('https://connect.facebook.net/en_US/fbevents.js');
  }
  w.fbq('init', META_PIXEL_ID);
  w.fbq('track', 'PageView');
};

type ConsentListener = (granted: { analytics: boolean; advertising: boolean }) => void;
const listeners = new Set<ConsentListener>();
let current = { analytics: false, advertising: false };

/** Subscribe to the effective consent (called immediately with the current value). */
export const onConsent = (fn: ConsentListener) => {
  listeners.add(fn);
  fn(current);
  return () => { listeners.delete(fn); };
};

/**
 * Load whatever the consent allows. Scripts can't be unloaded, so withdrawing a
 * category that already loaded reloads the page to drop them.
 */
export const applyConsent = (c: CookieConsent, optInRequired: boolean) => {
  if (typeof window === 'undefined') return;
  const next = effectiveConsent(c, optInRequired);
  const revoked = (loaded.analytics && !next.analytics) || (loaded.advertising && !next.advertising);
  if (revoked) {
    window.location.reload();
    return;
  }
  if (next.analytics) loadAnalytics();
  if (next.advertising) loadAdvertising();
  if (next.analytics !== current.analytics || next.advertising !== current.advertising) {
    current = next;
    listeners.forEach(fn => fn(current));
  }
};
